import React, { FC, useMemo, useState } from 'react';
import { useLocation } from 'umi';
import { useLivPageTrigger, useResetPlatformsOnGameChange, ALL_SYSTEMS_PLATFORM } from '../../hooks';
import Filter from './component/Filter';
import LiveCardWithRank from './component/LiveCardWithRank';
import { useRequestController } from '@sg/hooks/useRequestController';
import moment from 'moment';
import Abnormal from '@home/components/Abnormal';
import MarqueeBanner from '@sg/components/MarqueeBanner';
interface Props {
  studio: {
    name: string;
    unified_id: string;
  };
}

const LiveVisualizationPage: FC<Props> = (props) => {
  const location = useLocation();
  const {studio} = props; 
  const triggerCfg = useLivPageTrigger();
  const {
    selectedPlatforms,
    selectedRegion,
    selectedDate,
    handleComparisonChange,
  } = triggerCfg || {};
  const gameTitle = studio?.name && studio.name !== 'All' ? studio.name : null;

  useResetPlatformsOnGameChange(
    gameTitle,
    () => handleComparisonChange([ALL_SYSTEMS_PLATFORM]),
  );
  /** Notes use the range end month from the top Month filter (same grain as Portfolio Overview). */
  const notesSelectedMonthMoment = useMemo(() => {
    if (!selectedDate?.[1]) return null;
    return moment(selectedDate[1]).startOf('month');
  }, [selectedDate]);

  const notesParams = useMemo(
    () => ({
      game_title: studio.name,
      source: 'live_titles',
    }),
    [studio.name],
  );

  const notesMaxDataTime = notesSelectedMonthMoment?.format('YYYY-MM') ?? null;

  const fetchParams = useMemo(
    () => ({
      disable_pagination: 1,
      metrics: [
        'gross_revenue',
        'gross_base_sales',
        'gross_gaas_sales',
        'net_revenue',
        'net_base_sales',
        'net_gaas_sales',
        'unit_sales',
        'base_sales',
        'dlc_sales',
        'mau',
        'avg_dau',
        'pcu',
        'new_users',
        'returned_users',
        'retained_users',
        'payers',
        'payers_ratio',
        'arpu',
        'arppu',
      ],
      // game_title: ['Path of Exile 2'], // studio.name
      game_title: [studio.name], // studio.name
      country_code: selectedRegion.markets,
      start_date: moment(selectedDate[0]).format('YYYY-MM-DD'),
      end_date: moment(selectedDate[1]).format('YYYY-MM-DD'),
      include_yoy: 1,
      include_mom: 1,
      os_metrics: ['gross_revenue', 'net_revenue', 'unit_sales'],
      os: selectedPlatforms,
    }),
    [selectedPlatforms, selectedRegion, selectedDate, studio],
  );
  const [loadingStat, setLoadingStat] = useState('pending');
  const [switcher, setSwitcher] = useState('gross_revenue');
  const eventsParams = useMemo(
    () => ({
      game_title: studio.name,
    }),
    [studio.name],
  );
  
  // Only make API calls when studio.name is available
  const isReady = Boolean(studio?.name);
  
  const { data: summaryData, loading, mutate } = useRequestController({
    method: 'POST',
    url: '/api/v1/globalpedia_pc/studio_monthly_report/mgmt/live_title/summary',
    refreshDeps: fetchParams,
    ready: isReady,
    cacheKey: 'visualization',
    onBefore: () => {
      mutate({});
    },
    onFinally: () => {
      setLoadingStat('success');
    }
  });
  const { data: timeLineData, loading: timeLineLoading, error: isError } = useRequestController(
    {
      method: 'POST',
      url: '/api/v1/globalpedia_pc/studio_monthly_report/mgmt/live_title/timeline',
      refreshDeps: fetchParams,
      ready: isReady,
    },
  );
  const { data: eventsData, loading: eventsLoading } = useRequestController({
    method: 'POST',
    url: '/api/v1/mgmt_pc/studio/live-title/events',
    refreshDeps: eventsParams,
    ready: isReady,
    cacheKey: `live-title-events-${studio?.name || ''}`,
  });
  const domLoading = loadingStat ==='pending' ||  loading || timeLineLoading;
  const adBaseUrl = useMemo(
    () => `/v2${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );
  
  return (
    <div>
      <>
        <MarqueeBanner baseUrl={adBaseUrl} type="marquee" />
        <Filter {...(triggerCfg as any)} gameTitle={gameTitle} />
        <Abnormal
          isLoading={domLoading}
          isError={isError}
          loadingMsg='Loading'
          component={
            <LiveCardWithRank
              {...{
                summaryData,
                timeLineData,
                studio,
                loading: domLoading,
                metricTipsData: summaryData?.metric_definition,
                switcher,
                setSwitcher,
                eventsData,
                eventsLoading,
                selectedDate: selectedDate as any,
                notesParams,
                notesSelectedMonth: notesSelectedMonthMoment,
                notesMaxDataTime,
              }}
            />
          }
        />
      </>
      {/* {summaryData && JSON.stringify(summaryData) !== '{}' ?
       : <div style={{textAlign: 'center', height: '300px', lineHeight: '300px'}}>
          暂无数据
        </div>} */}
    </div>
  );
};

export default LiveVisualizationPage;
